import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Plus, Clock, MessageSquare, X, Loader2, Pencil, Trash2, CalendarClock } from 'lucide-react'
import { useEventTypes } from '@/hooks/useEventTypes'
import { useEventTypeMutations } from '@/hooks/useEventTypeMutations'
import { ColorPickerField } from '@/components/widget-keys/ColorPickerField'
import { DeleteButton } from '@/components/ui/action-buttons'
import { InfoTooltip } from '@/components/ui/info-tooltip'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageContent } from '@/components/ui/page'
import { DataView, DataViewToolbar } from '@/components/data-view'
import type { DataViewColumn, DataViewAction, ViewMode } from '@/components/data-view'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import type { EventTypeCreateDTO, EventTypeUpdateDTO } from '@/api/generated/models'

export const Route = createFileRoute('/appointments-configuration')({
  component: AppointmentsConfiguration,
  errorComponent: RouteErrorFallback,
})

// TypeScript interfaces
export interface Question {
  id: string
  text: string
}

export interface BookingType {
  id: string
  name: string
  description: string | null
  details: string | null
  defaultDuration: number // in minutes
  availableForBooking: boolean
  color: string
  questions: Question[]
}

const DEFAULT_COLOR = '#3B82F6'


// Helper function to serialize questions for storage - will be used when description field is added back
// const serializeQuestions = (questions: Question[]): string => {
//   if (questions.length === 0) return ''
//   return JSON.stringify(questions.map(q => q.text))
// }

function AppointmentsConfiguration() {
  const { data: eventTypes = [], isLoading, error } = useEventTypes()
  const { createEventType, updateEventType, deleteEventType } = useEventTypeMutations()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [selectedBookingType, setSelectedBookingType] = useState<BookingType | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    details: '',
    defaultDuration: 30,
    availableForBooking: true,
    color: DEFAULT_COLOR
  })
  const [formQuestions, setFormQuestions] = useState<Question[]>([])
  const [newQuestion, setNewQuestion] = useState('')
  const [showQuestionInput, setShowQuestionInput] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [availability, setAvailability] = useState<'all' | 'available' | 'unavailable'>('all')
  const [view, setView] = useLocalStorage<ViewMode>('audavo:view:event-types', 'cards')

  // Map event types to booking-type view models
  const bookingTypes = useMemo<BookingType[]>(
    () =>
      eventTypes.map(eventType => ({
        id: eventType.id,
        name: eventType.name,
        description: eventType.description || null,
        details: eventType.details || null,
        defaultDuration: eventType.duration_minutes,
        availableForBooking: eventType.booking_available,
        color: eventType.color || DEFAULT_COLOR,
        questions: [] // Questions will be separate from description
      })),
    [eventTypes],
  )

  const filteredBookingTypes = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    return bookingTypes.filter(type => {
      const matchesSearch =
        !query ||
        type.name.toLowerCase().includes(query) ||
        (type.description?.toLowerCase().includes(query) ?? false)
      const matchesAvailability =
        availability === 'all' ||
        (availability === 'available' ? type.availableForBooking : !type.availableForBooking)
      return matchesSearch && matchesAvailability
    })
  }, [bookingTypes, searchTerm, availability])

  const handleAddBookingType = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const createData: EventTypeCreateDTO = {
      name: formData.name,
      description: formData.description || null,
      details: formData.details || null,
      duration_minutes: formData.defaultDuration,
      booking_available: formData.availableForBooking,
      color: formData.color
    }
    
    try {
      await createEventType.mutateAsync(createData)
      setDialogOpen(false)
      setFormData({
        name: '',
        description: '',
        details: '',
        defaultDuration: 30,
        availableForBooking: true,
        color: DEFAULT_COLOR
      })
      setFormQuestions([])
      setNewQuestion('')
      setShowQuestionInput(false)
    } catch (error) {
      // Error handled in mutation
    }
  }

  const handleAddFormQuestion = () => {
    if (!newQuestion.trim()) return
    
    const question: Question = {
      id: Date.now().toString(),
      text: newQuestion.trim()
    }
    
    setFormQuestions([...formQuestions, question])
    setNewQuestion('')
    setShowQuestionInput(false)
  }

  const handleRemoveFormQuestion = (questionId: string) => {
    setFormQuestions(prev => prev.filter(q => q.id !== questionId))
  }

  // Handle card click to open edit drawer
  const handleCardClick = (bookingType: BookingType) => {
    setSelectedBookingType(bookingType)
    setFormData({
      name: bookingType.name,
      description: bookingType.description || '',
      details: bookingType.details || '',
      defaultDuration: bookingType.defaultDuration,
      availableForBooking: bookingType.availableForBooking,
      color: bookingType.color
    })
    setFormQuestions(bookingType.questions)
    setDetailDialogOpen(true)
  }

  // Handle update submission
  const handleUpdateBookingType = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedBookingType) return
    
    const updateData: EventTypeUpdateDTO = {
      name: formData.name,
      description: formData.description || null,
      details: formData.details || null,
      duration_minutes: formData.defaultDuration,
      booking_available: formData.availableForBooking,
      color: formData.color
    }
    
    try {
      await updateEventType.mutateAsync({ id: selectedBookingType.id, data: updateData })
      handleDetailDialogClose()
    } catch (error) {
      // Error handled in mutation
    }
  }

  // Handle delete
  const handleDelete = async (bookingType: BookingType) => {
    return new Promise<void>((resolve, reject) => {
      deleteEventType.mutate(bookingType.id, {
        onSuccess: () => {
          if (detailDialogOpen && selectedBookingType?.id === bookingType.id) {
            setDetailDialogOpen(false)
            setSelectedBookingType(null)
          }
          resolve()
        },
        onError: (error) => reject(error)
      })
    })
  }

  // Handle closing detail dialog
  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false)
    setSelectedBookingType(null)
    setFormQuestions([])
    setNewQuestion('')
    setShowQuestionInput(false)
  }

  const columns: DataViewColumn<BookingType>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: type => <span className="font-medium">{type.name}</span>,
    },
    {
      id: 'duration',
      header: 'Dauer',
      cell: type => <Badge variant="secondary">{type.defaultDuration} Minuten</Badge>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: type => (
        <Badge variant={type.availableForBooking ? 'default' : 'secondary'}>
          {type.availableForBooking ? 'Verfügbar' : 'Nicht verfügbar'}
        </Badge>
      ),
    },
    {
      id: 'color',
      header: 'Farbe',
      cell: type => (
        <Badge variant="outline" className="gap-1.5 font-mono">
          <span className="size-2.5 rounded-full" style={{ backgroundColor: type.color }} />
          {type.color}
        </Badge>
      ),
    },
  ]

  const renderBookingCard = (bookingType: BookingType) => (
    <Card className="h-full hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl">{bookingType.name}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="gap-1.5 font-mono">
              <span className="size-2.5 rounded-full" style={{ backgroundColor: bookingType.color }} />
              {bookingType.color}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Standard-Dauer</span>
          </div>
          <Badge variant="secondary">
            {bookingType.defaultDuration} Minuten
          </Badge>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
            <span>Status</span>
          </div>
          <Badge variant={bookingType.availableForBooking ? 'default' : 'secondary'}>
            {bookingType.availableForBooking ? 'Verfügbar' : 'Nicht verfügbar'}
          </Badge>
        </div>

        {false && (
        <div>
          <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
            <MessageSquare className="h-4 w-4" />
            <span>Spezielle Fragen</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {bookingType.questions.length > 0 ? (
              <>
                <Badge variant="secondary">
                  {bookingType.questions[0].text}
                </Badge>
                {bookingType.questions.length > 1 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Badge variant="outline" className="cursor-help hover:bg-muted">
                        +{bookingType.questions.length - 1}
                      </Badge>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Weitere Fragen:</h4>
                        {bookingType.questions.slice(1).map((question, index) => (
                          <div key={question.id} className="flex items-start gap-2">
                            <span className="text-xs text-muted-foreground mt-0.5">
                              {index + 2}.
                            </span>
                            <span className="text-sm">{question.text}</span>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Keine Fragen</span>
            )}
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  )

  const actions: DataViewAction<BookingType>[] = [
    {
      id: 'edit',
      label: 'Bearbeiten',
      icon: Pencil,
      onSelect: handleCardClick,
    },
    {
      id: 'delete',
      label: 'Löschen',
      icon: Trash2,
      variant: 'destructive',
      onSelect: type => { void handleDelete(type) },
      confirm: {
        title: 'Termintyp löschen?',
        description: type =>
          `Sind Sie sicher, dass Sie den Termintyp "${type.name}" löschen möchten? Dieser Vorgang kann nicht rückgängig gemacht werden.`,
      },
    },
  ]

  return (
    <PageContainer padding="p-4 sm:p-8">
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Termine</PageHeaderHeading>
              <PageHeaderDescription>Verwalten Sie Ihre Terminarten und deren Eigenschaften</PageHeaderDescription>
            </PageHeaderTitle>
            <DataViewToolbar
              view={view}
              onViewChange={setView}
              search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Terminart suchen…' }}
              filters={
                <Select value={availability} onValueChange={value => setAvailability(value as typeof availability)}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle</SelectItem>
                    <SelectItem value="available">Verfügbar</SelectItem>
                    <SelectItem value="unavailable">Nicht verfügbar</SelectItem>
                  </SelectContent>
                </Select>
              }
              toolbarAction={
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Terminart hinzufügen
                </Button>
              }
            />
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6 overflow-y-auto">
          <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
          <SheetContent side="right" className="w-full sm:max-w-md">
            <form onSubmit={handleAddBookingType} className="flex flex-1 flex-col overflow-hidden">
              <SheetHeader>
                <SheetTitle>Neue Terminart hinzufügen</SheetTitle>
                <SheetDescription>
                  Erstellen Sie eine neue Terminart mit den gewünschten Eigenschaften.
                </SheetDescription>
              </SheetHeader>
              <div className="flex flex-col gap-4 px-4 py-2 flex-1 min-h-0 overflow-y-auto">
                <div className="grid gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="flex flex-1 max-h-72 flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="description">Anweisungen an KI Agenten</Label>
                    <InfoTooltip content="Diese Anweisung steht nur dem KI Agenten zur Verfügung. Sie dient dazu zu beschreiben, bei welchen Anliegen diese Terminart vergeben werden soll." />
                  </div>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Optionale Beschreibung für diesen Termintyp"
                    className="flex-1"
                  />
                </div>
                <div className="flex flex-1 max-h-72 flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="details">Öffentliche Beschreibung</Label>
                    <InfoTooltip content="Diese Beschreibung wird Kunden auf der öffentlichen Buchungsseite unter der Terminart angezeigt." />
                  </div>
                  <Textarea
                    id="details"
                    value={formData.details}
                    onChange={(e) => setFormData({...formData, details: e.target.value})}
                    placeholder="Wird Kunden auf der öffentlichen Buchungsseite angezeigt"
                    className="flex-1"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="duration">Standard-Dauer (Minuten)</Label>
                  <Input
                    id="duration"
                    type="number"
                    min="15"
                    max="240"
                    step="15"
                    value={formData.defaultDuration}
                    onChange={(e) => setFormData({...formData, defaultDuration: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="available"
                    checked={formData.availableForBooking}
                    onCheckedChange={(checked) => setFormData({...formData, availableForBooking: checked})}
                  />
                  <Label htmlFor="available">Für Buchung verfügbar</Label>
                </div>
                <ColorPickerField
                  label="Farbe (im Kalender)"
                  value={formData.color}
                  onChange={(value) => setFormData({ ...formData, color: value ?? DEFAULT_COLOR })}
                  defaultColor={DEFAULT_COLOR}
                />
                {false && (
                <div className="grid gap-2">
                  <Label>Spezielle Fragen</Label>
                  <div className="space-y-2">
                    {formQuestions.map(question => (
                      <div key={question.id} className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
                        <span className="flex-1">{question.text}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFormQuestion(question.id)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {showQuestionInput ? (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Frage eingeben"
                          value={newQuestion}
                          onChange={(e) => setNewQuestion(e.target.value)}
                          className="text-base md:text-sm"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              handleAddFormQuestion()
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddFormQuestion}
                          disabled={!newQuestion.trim()}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowQuestionInput(false)
                            setNewQuestion('')
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowQuestionInput(true)}
                        className="w-fit"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Frage hinzufügen
                      </Button>
                    )}
                  </div>
                </div>
                )}
              </div>
              <SheetFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={createEventType.isPending}>
                  {createEventType.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Terminart anlegen
                </Button>
              </SheetFooter>
            </form>
          </SheetContent>
          </Sheet>
      {/* Detail Drawer */}
      <Sheet open={detailDialogOpen} onOpenChange={handleDetailDialogClose}>
        <SheetContent side="right" className="w-full sm:max-w-lg">
          {selectedBookingType && (
            <form onSubmit={handleUpdateBookingType} className="flex flex-1 flex-col overflow-hidden">
                  <SheetHeader>
                    <SheetTitle>Termintyp bearbeiten</SheetTitle>
                  </SheetHeader>

                  <div className="flex flex-col gap-4 px-4 flex-1 min-h-0 overflow-y-auto">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Name</Label>
                      <Input
                        id="edit-name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div className="flex flex-1 max-h-72 flex-col gap-2">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="edit-description">Anweisungen an KI Agenten</Label>
                        <InfoTooltip content="Diese Anweisung steht nur dem KI Agenten zur Verfügung. Sie dient dazu zu beschreiben, bei welchen Anliegen diese Terminart vergeben werden soll." />
                      </div>
                      <Textarea
                        id="edit-description"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Optionale Beschreibung für diesen Termintyp"
                        className="flex-1"
                      />
                    </div>
                    <div className="flex flex-1 max-h-72 flex-col gap-2">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="edit-details">Öffentliche Beschreibung</Label>
                        <InfoTooltip content="Diese Beschreibung wird Kunden auf der öffentlichen Buchungsseite unter der Terminart angezeigt." />
                      </div>
                      <Textarea
                        id="edit-details"
                        value={formData.details}
                        onChange={(e) => setFormData({...formData, details: e.target.value})}
                        placeholder="Wird Kunden auf der öffentlichen Buchungsseite angezeigt"
                        className="flex-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-duration">Standard-Dauer (Minuten)</Label>
                      <Input
                        id="edit-duration"
                        type="number"
                        min="15"
                        max="240"
                        step="15"
                        value={formData.defaultDuration}
                        onChange={(e) => setFormData({...formData, defaultDuration: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="edit-available"
                        checked={formData.availableForBooking}
                        onCheckedChange={(checked) => setFormData({...formData, availableForBooking: checked})}
                      />
                      <Label htmlFor="edit-available">Für Buchung verfügbar</Label>
                    </div>
                    <ColorPickerField
                      label="Farbe (im Kalender)"
                      value={formData.color}
                      onChange={(value) => setFormData({ ...formData, color: value ?? DEFAULT_COLOR })}
                      defaultColor={DEFAULT_COLOR}
                    />
                    {false && (
                    <div className="space-y-2">
                      <Label>Spezielle Fragen</Label>
                      <div className="space-y-2">
                        {formQuestions.map(question => (
                          <div key={question.id} className="flex items-center gap-2 p-2 bg-muted rounded-md text-sm">
                            <span className="flex-1">{question.text}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveFormQuestion(question.id)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                        {showQuestionInput ? (
                          <div className="flex gap-2">
                            <Input
                              placeholder="Frage eingeben"
                              value={newQuestion}
                              onChange={(e) => setNewQuestion(e.target.value)}
                              className="text-base md:text-sm"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  handleAddFormQuestion()
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleAddFormQuestion}
                              disabled={!newQuestion.trim()}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowQuestionInput(false)
                                setNewQuestion('')
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowQuestionInput(true)}
                            className="w-fit"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Frage hinzufügen
                          </Button>
                        )}
                      </div>
                    </div>
                    )}
                  </div>

                  <SheetFooter className="flex-row items-center gap-2">
                    <DeleteButton
                      variant="ghost"
                      className="mr-auto text-destructive hover:text-destructive hover:bg-destructive/10"
                      onDelete={() => handleDelete(selectedBookingType)}
                      itemName={selectedBookingType.name}
                      confirmationTitle="Termintyp löschen?"
                      confirmationDescription={`Sind Sie sicher, dass Sie den Termintyp "${selectedBookingType.name}" löschen möchten? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleDetailDialogClose}
                      disabled={updateEventType.isPending}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      type="submit"
                      disabled={updateEventType.isPending}
                    >
                      {updateEventType.isPending && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Termintyp aktualisieren
                    </Button>
                  </SheetFooter>
                </form>
          )}
        </SheetContent>
      </Sheet>

      <DataView<BookingType>
        items={filteredBookingTypes}
        getRowId={type => type.id}
        columns={columns}
        renderCard={renderBookingCard}
        actions={actions}
        onItemClick={handleCardClick}
        storageKey="audavo:view:event-types"
        defaultView="cards"
        view={view}
        onViewChange={setView}
        isLoading={isLoading}
        error={error}
        emptyState={{
          icon: CalendarClock,
          title: 'Keine Terminarten',
          description: 'Legen Sie Ihre erste Terminart an, um Buchungen zu ermöglichen.',
          action: (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Terminart hinzufügen
            </Button>
          ),
        }}
      />
        </PageContent>
      </PageCard>
    </PageContainer>
  )
}